// The meeting recorder's browser wiring (BUILD 1 WP-4).
//
// The only file that names the speech client's real modules (C-7: capture
// core, microphone owner, device spool, mint and session socket)
// and the browser APIs the recorder uses. Everything else reaches them
// through meeting-recorder-deps.ts, so tests install fakes instead.
// `installBrowserMeetingRecorder()` is idempotent and leaves installed deps
// (a test's fakes) alone. Owner: WP-4.
import { isNativeShell, wsUrl } from '@/lib/shell';
import { desktopRecordingBridge } from '@/lib/desktop-recording';
import { isDesktopShell } from '@/lib/desktop';
import { captureSupport, startCapture } from '@/speech/capture/core';
import { openDesktopAudio } from '@/speech/capture/desktop-audio';
import { micOwner } from '@/speech/capture/mic-owner';
import { speechSpool } from '@/speech/spool';
import { mintSpeechSession } from '@/speech/transport/mint';
import { SpeechSocket } from '@/speech/transport/session-socket';
import { sha256Hex } from './meeting-capture-chunks';
import {
  installMeetingRecorderDeps,
  meetingRecorderDeps,
  type MeetingRecorderDeps,
  type TakeLock,
} from './meeting-recorder-deps';
import { meetingsApi } from './meetings-api';

const DEVICE_KEY = 'poneva.meeting.device';

const hex = (buffer: ArrayBuffer) =>
  [...new Uint8Array(buffer)].map((byte) => byte.toString(16).padStart(2, '0')).join('');

function randomId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const value = hex(bytes.buffer);
  return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
}

let pageDevice: string | null = null;
/** This browser profile's id for take ownership; a per-viewer convenience, never authority. */
function deviceId(): string {
  try {
    const stored = localStorage.getItem(DEVICE_KEY);
    if (stored) return stored;
    const created = randomId();
    localStorage.setItem(DEVICE_KEY, created);
    return created;
  } catch {
    return (pageDevice ??= randomId());
  }
}

const media = (query: string) =>
  typeof matchMedia === 'function' ? matchMedia(query).matches : false;

/** Phones and tablets, where a meeting tab cannot be shared. */
function isMobile(): boolean {
  const agent = navigator as Navigator & { userAgentData?: { mobile?: boolean } };
  return (
    isNativeShell() ||
    agent.userAgentData?.mobile === true ||
    (media('(pointer: coarse)') && !media('(any-pointer: fine)'))
  );
}

type LockManagerLike = {
  request(
    name: string,
    options: { ifAvailable: boolean },
    callback: (lock: unknown) => Promise<void> | undefined,
  ): Promise<unknown>;
};

/** Web Locks: held until released, and released by the browser when the tab goes away. */
const webTakeLock: TakeLock = {
  tryHold(name) {
    const locks = (navigator as Navigator & { locks?: LockManagerLike }).locks;
    // Without Web Locks a second tab cannot be told apart; recover as before.
    if (!locks?.request) return Promise.resolve(() => {});
    return new Promise((resolve) => {
      locks
        .request(`poneva-meeting-take:${name}`, { ifAvailable: true }, (lock) => {
          if (!lock) {
            resolve(null);
            return undefined;
          }
          return new Promise<void>((release) => resolve(() => release()));
        })
        .catch(() => resolve(() => {}));
    });
  },
};

export function browserMeetingRecorderDeps(): MeetingRecorderDeps {
  const nav = navigator as Navigator & { wakeLock?: MeetingRecorderDeps['wakeLock'] };
  return {
    startCapture: (options) =>
      startCapture(options, desktopSystem ? { getSystemAudio: openDesktopAudio } : {}),
    micOwner,
    spool: speechSpool,
    mint: (request) => mintSpeechSession(request),
    openSocket: (options) => new SpeechSocket(options),
    wsUrl,
    captureSupport: () => ({
      ...captureSupport(),
      ...(isDesktopShell() ? { tab: false } : {}),
      desktopSystem,
    }),
    api: meetingsApi,
    sha256: sha256Hex,
    storage: nav.storage ?? null,
    wakeLock: nav.wakeLock ?? null,
    takeLock: webTakeLock,
    deviceId,
    uuid: randomId,
    isMobile,
    now: () => Date.now(),
    sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    random: () => Math.random(),
    pageEvents: typeof window === 'undefined' ? null : window,
  };
}

/** Called when the workspace mounts the recording strip. */
let desktopSystem = false;
export function installBrowserMeetingRecorder() {
  if (meetingRecorderDeps() || typeof window === 'undefined') return;
  const deps = browserMeetingRecorderDeps();
  installMeetingRecorderDeps(deps);
  const bridge = desktopRecordingBridge();
  if (bridge)
    void bridge
      .getInfo()
      .then((info) => {
        desktopSystem = info.recording?.systemAudio === true;
        if (meetingRecorderDeps() === deps) installMeetingRecorderDeps(deps);
      })
      .catch(() => {});
}
